import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Assumptions } from '../static/Assumptions';
import { Customer } from '../customer/Customer';

//Shanks: Need to clean up and seperate out into CP Profile and Haven PROFILE
@supertypeClass({ toClient: true, toServer: true })
export class Profile extends Everything {
  @property()
  personality: string = 'none';

  @property({
    rule: ['required', 'numericInt'],
    validate: 'isWithin(0, 20)',
    values: [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '11',
      '12',
      '13',
      '14',
      '15',
      '16',
      '17',
      '18',
      '19',
      '20',
    ],
    descriptions: function () {
      const desc = {
        '0': 'None',
        '1': '1',
        '2': '2',
        '3': '3',
        '4': '4',
        '5': '5',
        '6': '6',
        '7': '7',
        '8': '8',
        '9': '9',
        '10': '10',
        '11': '11',
        '12': '12',
        '13': '13',
        '14': '14',
        '15': '15',
        '16': '16',
        '17': '17',
        '18': '18',
        '19': '19',
        '20': '20',
      };
      //Shanks - Subclass this for Haven
      if (this.customer && this.customer.constructor && this.customer.constructor.name === 'HavenCustomer') {
        desc['0'] = '0';
      }
      return desc;
    },
  })
  numberOfChildren: number;

  @property({ rule: ['numeric'] })
  zipCode: number = 10028;

  @property({ values: Assumptions.stateValues })
  state: string = 'NY';

  @property()
  livingMethod: string = 'rent';

  @property({ values: { single: 'Single', married: 'Married', partner: 'Have a partner' } })
  relationship: string = 'single';

  @property({
    isLocal: true,
    values: {
      single: 'Single',
      singleparent: 'Single Parent',
      married: 'Married',
      marriedwithchildren: 'Married with Kids',
    },
  })
  @property({ isLocal: true })
  private _familyStatus: string;

  @property({
    isLocal: true,
    values: ['single', 'singleparent', 'married', 'marriedwithchildren'],
    descriptions: function () {
      let descriptions = {
        single: 'Single',
        singleparent: 'Single Parent',
        married: 'Married',
        marriedwithchildren: 'Married with Kids',
      };
      // Shanks - subclass this for Haven
      if (this.customer && this.customer.constructor && this.customer.constructor.name === 'HavenCustomer') {
        descriptions = {
          single: 'Me',
          singleparent: 'Me and my kids',
          married: 'Me and my partner',
          marriedwithchildren: 'Me, my partner, and kids',
        };
      }
      return descriptions;
    },
  })
  get familyStatus(): string {
    if (this.numberOfChildren === 0) {
      return this.relationship;
    } else {
      if (this.relationship === 'single') {
        return 'singleparent';
      } else {
        return 'marriedwithchildren';
      }
    }
  }

  set familyStatus(status: string) {
    if (status === 'marriedwithchildren') {
      this.numberOfChildren = 1;
      this.relationship = 'married';
      this.relationshipTrigger();
    } else if (status === 'singleparent') {
      this.numberOfChildren = 1;
      this.relationship = 'single';
      this.relationshipTrigger();
    } else {
      this.relationship = status;
      this.relationshipTrigger();
      this.numberOfChildren = 0;
    }

    this.numberOfChildrenTrigger();
  }

  @property()
  savingsRate: number;

  @property()
  savingsRate401K: number;

  @property({
    values: {
      none: 'none',
      community2yr: '2 year community',
      inState4yr: '4 year public (in-state)',
      outState4yr: '4 year public (out-of-state)',
      private4yr: '4 year private',
    },
  })
  collegeType: string = 'inState4yr';

  @property()
  includeBoard: boolean = true;

  @property()
  includeLiving: boolean;

  @property()
  collegeMaxYears: number = 4;

  @property({
    values: {
      '0': 'None',
      '1': '100%',
      '0.1': '10%',
      '0.2': '20%',
      '0.3': '30%',
      '0.4': '40%',
      '0.5': '50%',
      '0.6': '60%',
      '0.7': '70%',
      '0.8': '80%',
      '0.9': '90%',
    },
  })
  collegePercent: number = 1;

  @property({ isLocal: true, getType: () => Number })
  collegePercentOrder: Array<number> = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0];

  @property()
  policyType: string;

  @property()
  notifyPermanent: boolean;

  @property()
  childCareGiver: string = 'other';

  @property({ getType: () => Customer })
  customer: Customer;

  constructor() {
    super();
  }

  static new(customer) {
    return new Profile().init(customer);
  }

  init(customer) {
    this.customer = customer;
    return this;
  }

  numberOfChildrenTrigger() {
    this.customer.capitalNeeds.replaceIncome = this.numberOfChildren > 0 || this.relationship !== 'single';
    this.customer.capitalNeeds.adjustChildren();
  }

  relationshipTrigger() {
    if (this.relationship === 'single') {
      this.customer.capitalNeeds.deleteAlternate();
    }

    this.customer.capitalNeeds.replaceIncome = this.numberOfChildren > 0 || this.relationship !== 'single';
    this.customer.customerSelection = 0;
  }

  collegeTypeTrigger() {
    this.customer.capitalNeeds.sendKidsToCollege = this.collegeType !== 'none';
  }

  getCollegeCost() {
    if (this.collegeType === 'none') {
      return 0;
    }

    const costs = this.customer.assumptions.collegeCosts[this.collegeType];

    return (
      costs.tuition * this.collegePercent +
      costs.books +
      (this.includeBoard ? costs.board : 0) +
      (this.includeLiving ? costs.living : 0)
    );
  }

  getAverageCostPerChild() {
    const kidCount = this.customer.capitalNeeds.children.length;

    return this.getTotalCollegeCost() / (kidCount > 0 ? kidCount : 1);
  }

  getTotalCollegeCost() {
    const kidCount = this.customer.capitalNeeds.children.length;
    let cost = 0;

    for (let ix = 0; ix < kidCount; ++ix) {
      cost += this.customer.capitalNeeds.children[ix].getFutureValue();
    }

    return cost;
  }

  createCopyWithCustomer(newCustomer) {
    return this.createCopy(
      function (_obj, _prop, template) {
        switch (template.__name__) {
          case 'Assumptions':
            return Assumptions;

          case 'Customer':
            return [newCustomer];

          // Don't traverse
          case 'HavenCustomer':
            return [newCustomer];

          // Don't traverse
          case 'CoverpathCustomer':
            return [newCustomer];

          // Don't traverse
          case 'Profile':
            return [newCustomer.profile];
        }
      }.bind(this)
    );
  }
}
